import { Button, Pill, useToast } from "@guestpilot/ui-kit";
import { useNavigate } from "react-router-dom";
import { ApiProblemError } from "../api/client";
import { useWrite } from "../api/hooks";
import { useHandleApiError } from "../errors";
import { formatDateTime } from "../format";
import { useI18n } from "../i18n";
import { kindOf, noticeText, relativeTime, SNOOZABLE, type InboxItem } from "../lib/inbox";
import { routeFor } from "../lib/pages";
import { useSession } from "../session";

/** One row per notice: what happened, when, and what to do (open its page, mark it read, postpone a review reminder). */
export function InboxList({ items }: { items: InboxItem[] }) {
  const { t, locale } = useI18n();
  const s = useSession();
  const nav = useNavigate();
  const toast = useToast();
  const fail = useHandleApiError();
  const tz = s.me.tenant.timeZone;
  // a notice about the plan limit alludes to an upgrade: not shown on Teams mobile
  items = s.teams.mobile ? items.filter((n) => n.type !== "upgrade_needed") : items;
  const read = useWrite("POST", "/api/v1/inbox/:id/read", ["/api/v1/inbox"]);
  const snooze = useWrite("POST", "/api/v1/review/items/:id/snooze", ["/api/v1/inbox"]);
  const open = (n: InboxItem) => {
    if (!n.readAt) read.mutate({ params: { id: n.id } }, { onError: fail });
    nav(routeFor(n.page, s.isTeamScopedOnly));
  };
  const date = (iso: string) => formatDateTime(iso, locale, tz);
  return (
    <ul className="gp-inbox" aria-label={t("ui:inbox.title")}>
      {items.map((n) => {
        const { title, body } = noticeText(t, (iso) => date(iso), n);
        const itemId = typeof n.params?.["itemId"] === "string" ? n.params["itemId"] : undefined;
        return (
          <li key={n.id} className="gp-inbox-row" data-unread={n.readAt ? "false" : "true"}>
            <span className="gp-inbox-dot" aria-hidden />
            <div className="gp-stack" style={{ gap: "var(--space-2)" }}>
              <span>
                <Pill>{t(`ui:inbox.kind.${kindOf(n.type)}`)}</Pill>
              </span>
              <span className="t-body-strong">{title}</span>
              {body ? <span className="t-body-sm gp-muted">{body}</span> : null}
              <span className="t-caption gp-faint" title={date(n.createdAt)}>
                {relativeTime(n.createdAt, new Date(), locale) ?? date(n.createdAt)}
              </span>
              <div className="gp-inbox-actions">
                <Button variant="ghost" onClick={() => open(n)}>
                  {t("ui:inbox.open")}
                </Button>
                {n.readAt ? null : (
                  <Button
                    variant="ghost"
                    onClick={() => read.mutate({ params: { id: n.id } }, { onError: fail })}
                  >
                    {t("ui:inbox.markRead")}
                  </Button>
                )}
                {n.type === "campaign_complete" && s.can("EXPORT_READ") ? (
                  <Button variant="ghost" onClick={() => nav("/exports")}>
                    {t("ui:inbox.viewEvidence")}
                  </Button>
                ) : null}
                {SNOOZABLE.has(n.type) && itemId && !n.readAt && s.can("REVIEW_DECIDE") ? (
                  <Button
                    variant="ghost"
                    onClick={() =>
                      snooze.mutate(
                        { params: { id: itemId } },
                        {
                          onSuccess: () => {
                            toast.success(t("ui:inbox.snoozed"));
                            read.mutate({ params: { id: n.id } });
                          },
                          onError: (e) =>
                            e instanceof ApiProblemError && e.status === 422
                              ? toast.error(t("ui:inbox.snoozeFailed"))
                              : fail(e),
                        },
                      )
                    }
                  >
                    {t("ui:inbox.snooze")}
                  </Button>
                ) : null}
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
